/**
 * 系统预设音色：与克隆音色列表一致使用 vendor + 平台 voice_id（阿里云文档中的 voice 参数）；
 * preset_voice_id 为系统预设 catalog id（slug，如 tina、cherry）。
 *
 * 至尊版 · 阿里云语音合成 TTS → Qwen3.5-Omni、Qwen3.5-Omni-Realtime
 * @see https://help.aliyun.com/zh/model-studio/omni-voice-list#afe9cac84a4q0
 *
 * 至尊版 · 阿里云语音合成 TTS (Lite) → Qwen3-Omni-Flash、Qwen3-Omni-Flash-Realtime
 * @see https://help.aliyun.com/zh/model-studio/omni-voice-list#773cc75ab7j98
 */

import { TRIPLE_ACCESS_PLATFORM_DEFAULTS } from './tripleAccessPlatform'

/** 各预设共用的示例试听地址 */
/** 预备地址：https://audio.bj.gooki.com/scene/694a9516-1482-4953-8a9f-6899ce0fea0c.wav */
// export const PRESET_SAMPLE_AUDIO_FILE_PATH = ''

function slugPresetId(voiceParam) {
  return String(voiceParam || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
}

/** @param {string} voiceParam 阿里云 voice 参数 */
function buildAliyunOmni35Preset(voiceParam, label, emoji = '👩') {
  return {
    id: slugPresetId(voiceParam),
    label,
    emoji,
    vendor: 'ali',
    voice_id: voiceParam,
    audio_file_path: '',
  }
}

/** @param {string} voiceParam 阿里云 voice 参数 */
function buildAliLiteFlashPreset(voiceParam, label, emoji = '👩') {
  return {
    id: slugPresetId(voiceParam),
    label,
    emoji,
    vendor: 'qwen3-omni-flash-realtime',
    voice_id: voiceParam,
    audio_file_path: '',
  }
}

/** 去重：同一 voice_id 仅保留一项 */
function dedupePresetsByVoiceId(list) {
  const seen = new Set()
  return list.filter((item) => {
    const key = String(item.voice_id || '').trim()
    if (!key || seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Qwen3.5-Omni / Qwen3.5-Omni-Realtime 官方音色（默认 Tina） */
const OMNI35_VOICE_ROWS = [
  ['Tina', '甜甜 Tina', '👩'],
  ['Cindy', '林欣宜 Cindy', '👩'],
  ['Liora Mira', '清欢 Liora Mira', '👩'],
  ['Sunnybobi', '知芝 Sunnybobi', '👩'],
  ['Raymond', '林川野 Raymond', '👨'],
  ['Ethan', '晨煦 Ethan', '👨'],
  ['Theo Calm', '予安 Theo Calm', '👨'],
  ['Serena', '苏瑶 Serena', '👩'],
  ['Harvey', '厚 Harvey', '👨'],
  ['Maia', '四月 Maia', '👩'],
  ['Evan', '江晨 Evan', '👨'],
  ['Qiao', '小乔妹 Qiao', '👩'],
  ['Momo', '茉兔 Momo', '👩'],
  ['Wil', '伟伦 Wil', '👨'],
  ['Angel', '台普 - 安琪 Angel', '👩'],
  ['Li Cassian', '东厂 - 李公公 Li Cassian', '👨'],
  ['Mia', '温柔生活博主 - 舒然 Mia', '👩'],
  ['Joyner', '喜剧担当 - 阿逗 Joyner', '👨'],
  ['Gold', '金爷 Gold', '👨'],
  ['Katerina', '卡捷琳娜 Katerina', '👩'],
  ['Ryan', '甜茶 Ryan', '👨'],
  ['Jennifer', '詹妮弗 Jennifer', '👩'],
  ['Aiden', '艾登 Aiden', '👨'],
  ['Mione', '敏儿 Mione', '👩'],
  ['Sunny', '四川 - 晴儿 Sunny', '👩'],
  ['Dylan', '北京 - 晓东 Dylan', '👨'],
  ['Eric', '四川 - 程川 Eric', '👨'],
  ['Peter', '天津 - 李彼得 Peter', '👨'],
  ['Joseph Chen', '阿樸伯 Joseph Chen', '👨'],
  ['Marcus', '陕西 - 秦川 Marcus', '👨'],
  ['Li', '南京 - 老李 Li', '👨'],
  ['Kiki', '粤语-阿清 Kiki', '👩'],
  ['Rocky', '粤语 - 阿强 Rocky', '👨'],
  ['Sohee', '素熙 Sohee', '👩'],
  ['Lenn', '莱恩 Lenn', '👨'],
  ['Ono Anna', '小野杏 Ono Anna', '👩'],
  ['Sonrisa', '索尼莎 Sonrisa', '👩'],
  ['Bodega', '博德加 Bodega', '👨'],
  ['Emilien', '埃米尔安 Emilien', '👨'],
  ['Andre', '安德雷 Andre', '👨'],
  ['Radio Gol', '拉迪奥·戈尔 Radio Gol', '👨'],
  ['Alek', '阿列克 Alek', '👨'],
  ['Rizky', '阿力 Rizky', '👨'],
  ['Roya', '萝雅 Roya', '👩'],
  ['Arda', '阿尔达 Arda', '👨'],
  ['Hana', '阿幸 Hana', '👩'],
  ['Dolce', '多尔切 Dolce', '👨'],
  ['Jakub', '雅克 Jakub', '👨'],
  ['Griet', '海娜 Griet', '👩'],
  ['Eliška', '艾莉卡 Eliška', '👩'],
  ['Marina', '玛丽娜 Marina', '👩'],
  ['Siiri', '西芮 Siiri', '👩'],
  ['Ingrid', '林恩 Ingrid', '👩'],
  ['Sigga', '海娜 Sigga', '👩'],
  ['Bea', '雅娜 Bea', '👩'],
  ['Chloe', '思怡 Chloe', '👩'],
]

/** qwen-audio-3.0-realtime-plus / 千问实时语音Audio3.0-Plus 音色（默认 龙安千） */
const OMNI_FLASH_VOICE_ROWS = [
  ['longanqian', '龙安千', '👩'],
  ['longanlingxin', '龙安灵心', '👩'],
  ['longanlufeng', '龙安鲁风', '👨'],
  ['longanlingxi', '龙安灵汐', '👩'],
  ['longanxiaoxin', '龙安小欣', '👩'],
  ['longanfengyue', '龙安风月', '👩'],
  ['longanyuanfei', '龙安远飞', '👩'],
  ['longanhuan_v3.6', '龙安欢_v3.6', '👩'],
  ['longjielidou_v3.6', '龙洁莉豆_v3.6', '👨'],
  ['longpaopao_v3.6', '龙泡泡_v3.6', '👩'],
  ['longhuohuo_v3.6', '龙火火_v3.6', '👨'],
  ['longchuanshu_v3.6', '龙传书_v3.6', '👨'],
  ['loongmary', '龙玛丽', '👩'],
  ['loongeva_v3.6', '龙伊娃_v3.6', '👩'],
  ['loongjohn', '龙约翰', '👨'],
]

/** 已下线的旧版 preset_voice_id（不再映射到新音色） */
export const DEPRECATED_PRESET_VOICE_IDS = Object.freeze(
  new Set([
    'female_intellectual',
    'female_english',
    'female_ali_zhoujun',
    'male_kind',
    'male_fresh',
    'female_ali_lite',
    'female_ali_lite_english',
    'female_ali_lite_zhoujun',
  ]),
)

export const STALE_VOICE_MESSAGE = '原音色已失效，请重新克隆'

/** 是否为已下线的旧版系统 preset_voice_id（仅系统预设，不含「我的音色」） */
export function isDeprecatedSystemPresetVoiceId(presetVoiceId) {
  return DEPRECATED_PRESET_VOICE_IDS.has(String(presetVoiceId != null ? presetVoiceId : '').trim())
}

/** 试听/模拟演示前：若当前选中为旧版系统预设，返回提示文案；否则返回空字符串 */
export function getStaleVoiceAuditionMessage(voiceCall) {
  if (!voiceCall || typeof voiceCall !== 'object') return ''
  const presetId = String(voiceCall.presetVoiceId != null ? voiceCall.presetVoiceId : '').trim()
  if (!presetId || !isDeprecatedSystemPresetVoiceId(presetId)) return ''
  return STALE_VOICE_MESSAGE
}

/** 阿里云 TTS 预设（Qwen3.5-Omni 系列） */
export const PRESET_VOICES_ALIYUN = dedupePresetsByVoiceId(
  OMNI35_VOICE_ROWS.map(([voice, label, emoji]) => buildAliyunOmni35Preset(voice, label, emoji)),
)

/** 阿里云 TTS (Lite) 预设（Qwen3-Omni-Flash 系列） */
export const PRESET_VOICES_QWEN_OMNI = dedupePresetsByVoiceId(
  OMNI_FLASH_VOICE_ROWS.map(([voice, label, emoji]) =>
    buildAliLiteFlashPreset(voice, label, emoji),
  ),
)

/**
 * 语音引擎唯一配置源（新增引擎只改此处）
 * - id: 前端 voiceEngine / voice_engine
 * - presetVendor: 系统预设、运行时 TTS
 * - cloneVendor: 普通克隆 copyvoice 的 vendor
 */
const ENGINE_REGISTRY = {
  ALIYUN: {
    id: 'ali',
    presetVendor: 'ali',
    cloneVendor: 'ali',
    presets: PRESET_VOICES_ALIYUN,
    defaultPresetId: 'tina',
    supportsClone: true,
    resolveAliases: ['ali'],
  },
  QWEN_OMNI: {
    id: 'ali_lite',
    presetVendor: 'qwen3-omni-flash-realtime',
    cloneVendor: 'ali_lite',
    presets: PRESET_VOICES_QWEN_OMNI,
    defaultPresetId: 'longanqian',
    supportsClone: true,
    resolveAliases: ['ali_lite', 'qwen3.5-omni-flash-realtime', 'qwen3-omni-flash-realtime'],
  },
}

/** 三段式克隆（无系统预设，clone vendor 独立） */
const TRIPLE_CLONE_REGISTRY = {
  id: 'triple',
  cloneVendor: 'ali_cosyvoice',
}

/** 语音引擎 ID（写入 voiceEngine / voice_engine），由 ENGINE_REGISTRY 派生 */
export const VOICE_ENGINE = Object.freeze({
  ALIYUN: ENGINE_REGISTRY.ALIYUN.id,
  QWEN_OMNI: ENGINE_REGISTRY.QWEN_OMNI.id,
  TRIPLE: TRIPLE_CLONE_REGISTRY.id,
})

/** 三段式克隆 copyvoice 主 vendor */
export const TRIPLE_CLONE_VENDOR = TRIPLE_CLONE_REGISTRY.cloneVendor

/** 是否为「我的音色」里通过三段式克隆创建的条目 */
export function isTripleCloneVoice(row) {
  if (!row || typeof row !== 'object') return false
  const vendor = String(row.vendor || '').trim()
  if (vendor === TRIPLE_CLONE_VENDOR) return true
  const legacyEngine = row.engine != null ? String(row.engine).trim() : ''
  const legacyCloneMode = row.clone_mode != null ? String(row.clone_mode).trim() : ''
  return legacyEngine === VOICE_ENGINE.TRIPLE || legacyCloneMode === 'triple'
}

/** 克隆音色创建方式展示文案（与克隆弹窗引擎卡片标题一致） */
export const CLONE_VOICE_TYPE_LABEL = Object.freeze({
  [VOICE_ENGINE.ALIYUN]: '千问全模态3.5-Omni',
  [VOICE_ENGINE.QWEN_OMNI]: '千问实时语音Audio3.0-Plus',
  [VOICE_ENGINE.TRIPLE]: '级联语音大模型-G1',
})

function getTripleCloneProductLine(row) {
  if (!row || typeof row !== 'object') return ''
  if (row.product_line != null) return String(row.product_line).trim()
  if (row.productLine != null) return String(row.productLine).trim()
  return ''
}

function getTripleCloneTypeLabel(row) {
  const productLine = getTripleCloneProductLine(row)
  const catalog = TRIPLE_ACCESS_PLATFORM_DEFAULTS.llm.systemModelCatalog || []
  const hit = catalog.find(
    (item) => String(item && item.productLine != null ? item.productLine : '').trim() === productLine,
  )
  return (hit && hit.label) || CLONE_VOICE_TYPE_LABEL[VOICE_ENGINE.TRIPLE]
}

/** @param {object} row 克隆音色列表项 */
export function getCloneVoiceTypeLabel(row) {
  if (!row || typeof row !== 'object') return ''
  if (isTripleCloneVoice(row)) {
    return getTripleCloneTypeLabel(row)
  }
  const vendor = String(row.vendor || '').trim()
  for (const def of Object.values(ENGINE_REGISTRY)) {
    if (
      def.cloneVendor === vendor ||
      def.presetVendor === vendor ||
      def.resolveAliases.includes(vendor)
    ) {
      return CLONE_VOICE_TYPE_LABEL[def.id] || ''
    }
  }
  const engine = resolveVoiceEngine(row.engine != null ? row.engine : vendor)
  return CLONE_VOICE_TYPE_LABEL[engine] || CLONE_VOICE_TYPE_LABEL[VOICE_ENGINE.ALIYUN]
}

/**
 * 语音引擎运行时配置（key = engine id）
 * @type {Record<string, {
 *   vendor: string,
 *   cloneVendor: string,
 *   presets: typeof PRESET_VOICES_ALIYUN,
 *   defaultPresetId: string,
 *   supportsClone: boolean,
 *   resolveAliases: string[],
 * }>}
 */
export const VOICE_ENGINE_CONFIG = Object.fromEntries(
  Object.values(ENGINE_REGISTRY).map((def) => [
    def.id,
    {
      vendor: def.presetVendor,
      cloneVendor: def.cloneVendor,
      presets: def.presets,
      defaultPresetId: def.defaultPresetId,
      supportsClone: def.supportsClone,
      resolveAliases: def.resolveAliases,
    },
  ]),
)

/** 支持克隆音色的引擎 ID 列表 */
export const CLONE_VOICE_ENGINE_IDS = Object.keys(VOICE_ENGINE_CONFIG).filter(
  (id) => VOICE_ENGINE_CONFIG[id].supportsClone,
)

/** @param {string} engine voiceEngine、voice_engine 或 vendor */
export function resolveVoiceEngine(engine) {
  const e = String(engine || '').trim()
  if (!e) return VOICE_ENGINE.ALIYUN
  if (VOICE_ENGINE_CONFIG[e]) return e
  for (const [id, cfg] of Object.entries(VOICE_ENGINE_CONFIG)) {
    if (
      cfg.resolveAliases.includes(e) ||
      cfg.vendor === e ||
      (cfg.cloneVendor && cfg.cloneVendor === e)
    ) {
      return id
    }
  }
  return VOICE_ENGINE.ALIYUN
}

/** @param {string} engine */
export function getEngineVendor(engine) {
  const id = resolveVoiceEngine(engine)
  return VOICE_ENGINE_CONFIG[id]?.vendor || ENGINE_REGISTRY.ALIYUN.presetVendor
}

/** @param {string} engine */
export function getPresetVoicesByEngine(engine) {
  const id = resolveVoiceEngine(engine)
  return VOICE_ENGINE_CONFIG[id]?.presets || PRESET_VOICES_ALIYUN
}

/** 克隆音色是否属于当前语音引擎（不含三段式） */
export function cloneVoiceMatchesEngine(row, engine) {
  if (!row || typeof row !== 'object' || isTripleCloneVoice(row)) return false
  const engineId = resolveVoiceEngine(engine)
  const expectedVendor = getCloneVendor(engineId)
  const vendor = String(row.vendor || '').trim()
  if (vendor && vendor === expectedVendor) return true
  if (row.engine != null && String(row.engine).trim() !== '') {
    return resolveVoiceEngine(row.engine) === engineId
  }
  return resolveVoiceEngine(vendor) === engineId
}

function cloneGenderEmoji(gender) {
  const g = String(gender || '').trim().toLowerCase()
  if (g === 'male' || g === '男') return '👨'
  if (g === 'female' || g === '女') return '👩'
  return '🎙️'
}

/** 将「我的音色」中匹配当前引擎的克隆项转为预设卡片数据 */
export function clonedVoicesToPresetOptions(clonedVoices, engine) {
  const list = Array.isArray(clonedVoices) ? clonedVoices : []
  return list
    .filter((row) => cloneVoiceMatchesEngine(row, engine))
    .map((row) => {
      const voiceId = String(row.voice_id != null ? row.voice_id : row.id != null ? row.id : '')
        .trim()
      return {
        id: voiceId,
        label: String(row.name != null ? row.name : '').trim() || '未命名音色',
        emoji: cloneGenderEmoji(row.gender),
        isClone: true,
        vendor: String(row.vendor || '').trim(),
        voice_id: voiceId,
        audio_file_path: String(row.audio_file_path != null ? row.audio_file_path : '').trim(),
      }
    })
    .filter((item) => item.id)
}

/** 系统预设 + 同引擎克隆音色（用于「预设音色」网格） */
export function getPresetVoiceOptions(engine, clonedVoices) {
  const catalog = getPresetVoicesByEngine(engine).map((p) => ({ ...p, isClone: false }))
  return [...catalog, ...clonedVoicesToPresetOptions(clonedVoices, engine)]
}

/** 按 preset 选择 id 查找同引擎下的克隆音色原始条目 */
export function findEngineCloneVoiceById(clonedVoices, voiceId, engine) {
  const id = String(voiceId || '').trim()
  if (!id) return null
  const list = Array.isArray(clonedVoices) ? clonedVoices : []
  return (
    list.find((row) => {
      const rowId = String(row.voice_id != null ? row.voice_id : row.id != null ? row.id : '').trim()
      return rowId === id && cloneVoiceMatchesEngine(row, engine)
    }) || null
  )
}

/** @param {string} engine */
export function getDefaultPresetId(engine) {
  const id = resolveVoiceEngine(engine)
  return VOICE_ENGINE_CONFIG[id]?.defaultPresetId || PRESET_VOICES_ALIYUN[0].id
}

/** 克隆接口 copyvoice 的 vendor；不支持克隆的引擎回退阿里云 */
export function getCloneVendor(engine) {
  const e = String(engine || '').trim()
  if (e === VOICE_ENGINE.TRIPLE) return TRIPLE_CLONE_VENDOR
  const id = resolveVoiceEngine(engine)
  const cfg = VOICE_ENGINE_CONFIG[id]
  if (!cfg || !cfg.supportsClone) return VOICE_ENGINE_CONFIG[VOICE_ENGINE.ALIYUN].vendor
  return cfg.cloneVendor || cfg.vendor
}

/** preset_voice_id 是否为任一引擎的系统预设 catalog id */
export function isPresetCatalogVoiceId(presetVoiceId) {
  const pv = String(presetVoiceId || '').trim()
  if (!pv) return false
  return Object.keys(VOICE_ENGINE_CONFIG).some((engineId) =>
    VOICE_ENGINE_CONFIG[engineId].presets.some((p) => p.id === pv),
  )
}

/**
 * @param {string} presetVoiceId  catalog id，如 tina
 * @param {string} engine  voiceEngine
 */
export function getPresetCatalogEntry(presetVoiceId, engine) {
  const id = String(presetVoiceId || '').trim()
  if (!id) return null
  const list = getPresetVoicesByEngine(engine)
  return list.find((p) => p.id === id) || null
}
